import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getTenantSlug, tenantStorageKey, apiGetMe } from "@/lib/api";
import { useIsMobile } from "@/hooks/use-mobile";

export default function SsoCallback() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile === undefined) return; // wait for detection
    const loginPath = isMobile ? "/mobile/login" : "/login";

    const token = searchParams.get("token");
    const ssoError = searchParams.get("ssoError");

    // Immediately scrub the token (and any error param) from the URL bar so
    // it can't leak via browser history, share-sheet, referer headers, or
    // the Performance API. This must happen BEFORE any async work.
    if (token || ssoError) {
      try { window.history.replaceState({}, "", "/sso-callback"); } catch { /* ignore */ }
    }

    if (ssoError) {
      window.location.replace(`${loginPath}?ssoError=${encodeURIComponent(ssoError)}`);
      return;
    }

    if (!token) {
      window.location.replace(`${loginPath}?ssoError=${encodeURIComponent("SSO sign-in failed: missing token")}`);
      return;
    }

    const tenantSlug = getTenantSlug();

    (async () => {
      try {
        localStorage.setItem(tenantStorageKey("token"), token);

        const { user } = await apiGetMe(token, tenantSlug);
        localStorage.setItem(tenantStorageKey("user"), JSON.stringify(user));

        window.location.replace(isMobile ? "/mobile" : "/");
      } catch {
        localStorage.removeItem(tenantStorageKey("token"));
        window.location.replace(`${loginPath}?ssoError=${encodeURIComponent("SSO sign-in failed: could not load account")}`);
      }
    })();
  }, [searchParams, isMobile]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <div className="h-8 w-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">Completing sign-in…</p>
    </div>
  );
}
